import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import EventsTab from '../components/Attribution/EventsTab';
import { Loader2, RotateCcw, Globe, ServerCog, Database, ArrowRight, X, ChevronDown, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Kaynak Takibi > Toplanan veri: verinin nerede durduğu + ham satırlar (hosgelsin).
// Backend: glowytics-backend/src/routes/attribution.js (GET /raw).
const API = 'https://api.glowytics.com';
const BASE = `${API}/api/attribution`;

const TABLES = {
  touchpoints: {
    label: 'Dokunuşlar',
    desc: 'Ziyaretçi kampanyalı ya da başka bir siteden her geldiğinde bir satır. Doğrudan girişler yazılmaz.',
    cols: ['touch_at', 'visitor_id', 'session_id', 'channel', 'source', 'medium', 'campaign', 'consent', 'landing_page', 'referrer'],
  },
  orders: {
    label: 'Siparişler',
    desc: 'Her siparişe bir satır: kalıcı (A), oturum (B), ilk dokunuş ve atanan tek kaynak.',
    cols: ['order_id', 'created_at_site', 'status', 'total', 'consent', 'final_channel', 'final_origin', 'recovered_by_persistent',
      'a_channel', 'a_source', 'a_medium', 'a_campaign', 'b_channel', 'b_source', 'b_medium', 'first_channel', 'visitor_id',
      'session_count', 'pageview_count', 'is_test'],
  },
  events: {
    label: 'Sipariş olayları',
    desc: 'WordPress’ten gelen her bildirim: sipariş oluştu, durum değişti (iptal, iade…).',
    cols: ['received_at', 'order_id', 'old_status', 'new_status', 'total'],
  },
};

const fmtDate = (s) => {
  if (!s) return '-';
  const d = new Date(s);
  return isNaN(d) ? String(s) : d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};
const fmtBytes = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1).replace('.', ',')} MB` : `${Math.max(1, Math.round((b || 0) / 1024))} KB`);
const isTs = (c) => /(_at|_at_site)$/.test(c);

function cell(col, v) {
  if (v === null || v === undefined || v === '') return <span className="text-gray-300">-</span>;
  if (typeof v === 'boolean') return v ? <span className="text-indigo-700 font-medium">evet</span> : <span className="text-gray-400">hayır</span>;
  if (typeof v === 'object') return <span className="text-gray-500">{'{…}'}</span>;
  if (isTs(col)) return <span className="tabular-nums">{fmtDate(v)}</span>;
  return String(v);
}

const Box = ({ icon: Icon, step, title, where, children }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col gap-3 min-w-0">
    <div className="flex items-center gap-2">
      <span className="w-7 h-7 rounded-lg bg-gray-900 text-white flex items-center justify-center flex-shrink-0"><Icon size={15} /></span>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-gray-400">Adım {step}</div>
        <div className="text-sm font-semibold text-gray-900">{title}</div>
      </div>
    </div>
    <div className="text-xs text-gray-500">{where}</div>
    <div className="space-y-2">{children}</div>
  </div>
);

const Item = ({ k, children }) => (
  <div className="text-xs">
    <code className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-800 break-all">{k}</code>
    <div className="text-gray-600 mt-1">{children}</div>
  </div>
);

export default function AttributionData() {
  const { selectedBrand } = useAuth();
  const [table, setTable] = useState('touchpoints');
  const [visitorId, setVisitorId] = useState('');
  const [orderId, setOrderId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);
  const [sp, setSp] = useSearchParams();
  const mainTab = sp.get('tab') === 'olaylar' ? 'olaylar' : 'tablolar';

  const brandId = selectedBrand?.id;
  const brandOk = !!brandId;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams({ brandId, table, limit: '200' });
      if (visitorId) qs.set('visitor_id', visitorId);
      if (orderId) qs.set('order_id', orderId);
      const r = await fetch(`${BASE}/raw?${qs}`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData((await r.json()).data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [brandId, table, visitorId, orderId]);

  useEffect(() => { if (brandOk) load(); }, [load, brandOk]);
  useEffect(() => { setOpen(null); }, [table, visitorId, orderId]);

  if (!brandOk) {
    return <div className="p-6 flex justify-center text-gray-400"><Loader2 className="animate-spin" size={18} /></div>;
  }

  const tabBar = (
    <div className="flex items-center gap-1 border-b border-gray-200">
      {[['tablolar', 'Veri tabloları'], ['olaylar', 'Olaylar']].map(([k, l]) => (
        <button key={k} onClick={() => setSp(k === 'olaylar' ? { tab: 'olaylar' } : {})}
          className={`px-3 py-2 text-sm -mb-px border-b-2 ${mainTab === k ? 'border-gray-900 text-gray-900 font-medium' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>{l}</button>
      ))}
    </div>
  );

  if (mainTab === 'olaylar') {
    return (
      <div className="p-4 sm:p-6 space-y-5 max-w-[1500px] mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Toplanan Veri</h1>
            <p className="text-sm text-gray-500 max-w-3xl">Tarayıcıdan ve sunucudan gelen her olay (sayfa, ürün, sepet, ödeme, satın alma). Ziyaretçi kimliğine tıklayınca o kişinin akışı açılır.</p>
          </div>
          <Link to="/cro/kaynak-takibi" className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50">Rapora dön</Link>
        </div>
        {tabBar}
        <EventsTab />
      </div>
    );
  }

  const stats = data?.stats || {};
  const cfg = TABLES[table];
  const cols = cfg.cols.filter((c) => !data || data.columns.includes(c));

  const followVisitor = (v) => { setOrderId(''); setVisitorId(v); setTable('touchpoints'); };
  const followOrder = (o) => { setVisitorId(''); setOrderId(o); setTable('events'); };

  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-[1500px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Toplanan Veri</h1>
          <p className="text-sm text-gray-500 max-w-3xl">
            Kaynak takibinin topladığı her şey üç yerde duruyor: ziyaretçinin tarayıcısı, WordPress siparişi ve bizim veritabanımız.
            BigQuery ya da üçüncü taraf yok.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/cro/kaynak-takibi" className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50">Rapora dön</Link>
          <button onClick={load} className="p-1.5 border border-gray-300 rounded-lg bg-white hover:bg-gray-50" title="Yenile"><RotateCcw size={14} /></button>
        </div>
      </div>

      {tabBar}

      {/* Akış */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr_auto_1fr] gap-3 items-stretch">
        <Box icon={Globe} step={1} title="Ziyaretçinin tarayıcısı" where="hosgelsin.com · glowytics-attribution.js">
          <Item k="localStorage · glowy_attr_v1">Kalıcı kayıt (A), 30 gün. Ziyaretçi kimliği, ilk dokunuş, son kampanyalı dokunuş, oturum ve sayfa sayaçları.</Item>
          <Item k="sessionStorage · glowy_attr_s_v1">Oturum kaydı (B). Sekme kapanınca silinir; sadece bu oturumun giriş kaynağı.</Item>
          <Item k="çerez · glowy_attr">A ve B’nin özeti. Sipariş anında WordPress’in okuyabilmesi için.</Item>
          <Item k="çerez · glowy_consent">Banner seçimi: granted / denied. Yoksa “bilinmiyor”.</Item>
        </Box>
        <div className="hidden lg:flex items-center text-gray-300"><ArrowRight size={20} /></div>
        <Box icon={ServerCog} step={2} title="WordPress (sunucu)" where="mu-plugins/glowy-attribution.php · sipariş anında">
          <Item k="order meta · _glowy_attr">Çerezdeki A, B ve ilk dokunuş siparişin içine yazılır.</Item>
          <Item k="order meta · _glowy_visitor_id · _glowy_consent">Ziyaretçi kimliği ve izin durumu.</Item>
          <Item k="POST /api/attribution/wp/order">Sipariş ve her durum değişikliği HMAC imzalı olarak bize bildirilir. Arka plan kuyruğundan gider, ödemeyi bekletmez.</Item>
        </Box>
        <div className="hidden lg:flex items-center text-gray-300"><ArrowRight size={20} /></div>
        <Box icon={Database} step={3} title="Glowytics veritabanı" where="Postgres · hosgelsin şeması">
          {Object.entries(TABLES).map(([k, t]) => (
            <button key={k} onClick={() => { setVisitorId(''); setOrderId(''); setTable(k); }}
              className={`w-full text-left rounded-lg border p-2.5 transition ${table === k ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:bg-gray-50'}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-gray-900">{t.label}</span>
                <span className="text-sm font-semibold tabular-nums text-gray-900">{stats[k] ? stats[k].rows : '…'}</span>
              </div>
              <div className="text-[11px] text-gray-500 break-all">{stats[k]?.table?.split('.').pop() || ''}</div>
              <div className="text-[11px] text-gray-400">
                {stats[k]?.last_at ? `son yazım ${fmtDate(stats[k].last_at)} · ${fmtBytes(stats[k].bytes)}` : 'henüz satır yok'}
              </div>
            </button>
          ))}
        </Box>
      </div>
      <p className="text-xs text-gray-500">
        Dokunuşlar tarayıcıdan doğrudan <code className="px-1 rounded bg-gray-100">POST /api/attribution/touch</code> ile gelir, WordPress’ten geçmez.
        Tam şema adı: <code className="px-1 rounded bg-gray-100 break-all">{stats.orders?.table?.split('.')[0] || '…'}</code>
      </p>

      {/* Ham satırlar */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1">
              {Object.entries(TABLES).map(([k, t]) => (
                <button key={k} onClick={() => setTable(k)}
                  className={`px-3 py-1.5 text-sm rounded-lg ${table === k ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
                  {t.label} <span className="opacity-70 tabular-nums">{stats[k]?.rows ?? ''}</span>
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">{cfg.desc}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {visitorId && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-indigo-50 text-indigo-700 text-xs">
                Ziyaretçi: {visitorId.slice(0, 8)}…
                <button onClick={() => setVisitorId('')} title="Filtreyi kaldır"><X size={12} /></button>
              </span>
            )}
            {orderId && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-indigo-50 text-indigo-700 text-xs">
                Sipariş: #{orderId}
                <button onClick={() => setOrderId('')} title="Filtreyi kaldır"><X size={12} /></button>
              </span>
            )}
          </div>
        </div>

        {error && <div className="m-4 p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-700">Veri alınamadı: {error}</div>}

        {loading && !data ? (
          <div className="p-10 flex justify-center text-gray-400"><Loader2 className="animate-spin" size={20} /></div>
        ) : data && data.rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-500">Bu tabloda {visitorId || orderId ? 'bu filtreyle ' : ''}satır yok.</div>
        ) : data ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="w-8" />
                  {cols.map((c) => <th key={c} className="text-left font-medium px-3 py-2 whitespace-nowrap">{c}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.rows.map((r, i) => {
                  const isOpen = open === i;
                  return (
                    <React.Fragment key={r.id ?? i}>
                      <tr className="hover:bg-gray-50 align-top">
                        <td className="px-2 py-2">
                          <button onClick={() => setOpen(isOpen ? null : i)} className="text-gray-400 hover:text-gray-700" title="Satırın tamamı">
                            {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </button>
                        </td>
                        {cols.map((c) => (
                          <td key={c} className="px-3 py-2 whitespace-nowrap text-gray-800 max-w-[260px] truncate" title={typeof r[c] === 'string' ? r[c] : undefined}>
                            {c === 'visitor_id' && r[c] ? (
                              <button onClick={() => followVisitor(r[c])} className="text-indigo-700 hover:underline" title="Bu ziyaretçinin dokunuşları">{String(r[c]).slice(0, 8)}…</button>
                            ) : c === 'order_id' && r[c] ? (
                              <button onClick={() => followOrder(r[c])} className="text-indigo-700 hover:underline" title="Bu siparişin olayları">#{r[c]}</button>
                            ) : cell(c, r[c])}
                          </td>
                        ))}
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={cols.length + 1} className="bg-gray-50 px-4 py-3">
                            <div className="text-[11px] text-gray-500 mb-1">Satırın veritabanındaki hali (tüm kolonlar)</div>
                            <pre className="text-[11px] leading-relaxed text-gray-800 whitespace-pre-wrap break-all max-h-96 overflow-auto">{JSON.stringify(r, null, 2)}</pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
        {data && data.rows.length > 0 && (
          <div className="px-4 py-2 border-t border-gray-100 text-[11px] text-gray-400">
            En yeni {data.rows.length} satır gösteriliyor. Ziyaretçi kimliğine tıklayınca o kişinin dokunuşları, sipariş numarasına tıklayınca o siparişin olayları açılır.
          </div>
        )}
      </div>
    </div>
  );
}
